import React, { useState, useEffect, useRef } from "react";
import {
  Box, Button, Flex, Heading, FormControl, FormLabel, Input, Select,
  Textarea, Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, NumberInput, NumberInputField,
  useToast, Divider, Text, VStack, Container, InputGroup, InputRightElement, 
  Badge, Radio, RadioGroup, Stack, Alert, AlertIcon, Skeleton, Grid, GridItem, Spinner
} from "@chakra-ui/react";
import { DeleteIcon, DownloadIcon, SearchIcon, CheckIcon, AttachmentIcon } from "@chakra-ui/icons";
import { api, searchProdutosAutoComplete } from "../services/api"; 
import { useNavigate } from "react-router-dom";
import ProdutoImagem from "../components/ProdutoImagem";
import Preloader from "../components/Preloader";

const NovaListaPromocao = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    origem: "0", 
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: "",
    tipo_precificacao: "automatica",
  });

  const [manualItems, setManualItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const [descontoInput, setDescontoInput] = useState(""); 
  const [precoFinalInput, setPrecoFinalInput] = useState(""); 

  const [arquivo, setArquivo] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchTerm.length > 0 && !selectedProduct) {
        setIsSearching(true);
        try {
            const result = await searchProdutosAutoComplete(searchTerm);
            if(result) setFilteredProducts(result);
        } catch(e) { console.error(e); } 
        finally { setIsSearching(false); }
      } else {
        setFilteredProducts([]);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, selectedProduct]);

  useEffect(() => {
    setManualItems(prevItems => prevItems.map(item => {
        const isAuto = formData.tipo_precificacao === 'automatica';
        
        const novoPrecoOriginal = isAuto 
            ? parseFloat(item.valor_compra || 0) 
            : parseFloat(item.valor_venda || 0);

        const precoFinalMantido = parseFloat(item.preco_final || 0);

        let novoDesconto = 0;
        if (novoPrecoOriginal > 0) {
            novoDesconto = ((novoPrecoOriginal - precoFinalMantido) / novoPrecoOriginal) * 100;
        }

        return {
            ...item,
            preco_original: novoPrecoOriginal,
            desconto_percentual: novoDesconto.toFixed(2),
            preco_final: precoFinalMantido.toFixed(2)
        };
    }));
  }, [formData.tipo_precificacao]);

  const handleSelectProduct = (prod) => {
    setSelectedProduct(prod);
    setSearchTerm(prod.nome + " - " + prod.codigo_global); 
    setFilteredProducts([]); 
    setDescontoInput("");
    setPrecoFinalInput(""); 
  };

  const handleDownloadModel = () => {
    const header = "SKU_TAGA;CODIGO_FORNECEDOR;PRECO_COMPRA;PORCENTAGEM_DESCONTO";
    const row1 = "12345;;95.50;"; 
    const row2 = ";COD-ABC-99;;10.00";
    const csvContent = `${header}\n${row1}\n${row2}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "modelo_promocao_taga.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClearSelection = () => {
      setSelectedProduct(null);
      setSearchTerm("");
      setFilteredProducts([]);
  }

  const handleAddManualItem = () => {
    if (!selectedProduct) return;
    
    if (!descontoInput && !precoFinalInput) {
        toast({ title: "Informe o desconto (%) ou o preço final.", status: "warning" });
        return;
    }

    const precoOriginal = formData.tipo_precificacao === 'automatica' 
        ? parseFloat(selectedProduct.valor_compra) 
        : parseFloat(selectedProduct.valor_venda);

    let desconto = parseFloat(descontoInput) || 0;
    let final = parseFloat(precoFinalInput) || 0;

    if(final > 0) {
        desconto = ((precoOriginal - final) / precoOriginal) * 100;
    } else {
        final = precoOriginal - (precoOriginal * (desconto / 100));
    }

    const novoItem = {
        ...selectedProduct, 
        produto_id: selectedProduct.produto_id, 
        nome: selectedProduct.nome,
        codigo_global: selectedProduct.codigo_global,
        codigo_fornecedor: selectedProduct.codigo_fornecedor,
        fabricante_nome: selectedProduct.fabricante_nome,
        preco_original: precoOriginal,
        desconto_percentual: desconto.toFixed(2),
        preco_final: final.toFixed(2)
    };

    if(manualItems.find(i => i.produto_id === novoItem.produto_id)) {
        toast({ title: "Produto já adicionado.", status: "warning" });
        return;
    }

    setManualItems([...manualItems, novoItem]);
    handleClearSelection();
  };

  const handleRemoveItem = (index) => {
      const newItems = [...manualItems];
      newItems.splice(index, 1);
      setManualItems(newItems);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => { setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setArquivo(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.titulo || !formData.data_inicio) {
        toast({ title: "Preencha os campos obrigatórios.", status: "error" });
        return;
    }

    setLoading(true);
    const data = new FormData();
    data.append("titulo", formData.titulo);
    data.append("descricao", formData.descricao);
    data.append("origem", formData.origem);
    data.append("data_inicio", formData.data_inicio);
    data.append("tipo_precificacao", formData.tipo_precificacao);
    if(formData.data_fim) data.append("data_fim", formData.data_fim);

    if (arquivo) {
        data.append("arquivo", arquivo);
    } 
    else if (manualItems.length > 0) {
        manualItems.forEach((item, index) => {
            data.append(`itens_manual[${index}][produto_id]`, item.produto_id);
            data.append(`itens_manual[${index}][desconto_percentual]`, item.desconto_percentual);
            data.append(`itens_manual[${index}][preco_final]`, item.preco_final);
        });
    } else {
        toast({ title: "Adicione itens ou importe um arquivo.", status: "warning" });
        setLoading(false);
        return;
    }

    try {
        const res = await api.post("/lista-descontos", data, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        
        if(!res.data.erro) {
            toast({ title: "Lista criada com sucesso!", status: "success" });
            navigate("/promocoes");
        } else {
            throw new Error(res.data.msg);
        }
    } catch (error) {
        toast({ 
            title: "Erro ao criar lista.", 
            description: error.response?.data?.msg || "Verifique os dados.", 
            status: "error" 
        });
    } finally {
        setLoading(false);
    }
  };

  return (
    <Box bg="gray.50" minH="100vh" py={{ base: 4, md: 8 }}>
      <Container maxW="container.xl">
        <Heading size="lg" mb={6} color="orange.500" textAlign={{ base: "center", md: "left" }}>
            Nova Lista de Promoção
        </Heading>
        
        <Flex gap={6} direction={{ base: "column", lg: "row" }}>
            
            <Box flex="1" bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="sm" h="fit-content">
                <VStack spacing={4}>
                    <FormControl isRequired>
                        <FormLabel>Título</FormLabel>
                        <Input value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Descrição</FormLabel>
                        <Textarea value={formData.descricao} onChange={(e) => setFormData({...formData, descricao: e.target.value})} />
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel>Origem</FormLabel>
                        <Select value={formData.origem} onChange={(e) => setFormData({...formData, origem: e.target.value})}>
                            <option value="0">TAGA</option>
                            <option value="1">MORELATE</option>
                            <option value="2">VANNUCCI</option>
                            <option value="3">ISAPA</option>
                            <option value="4">BRCOMPANY</option>
                            <option value="5">AUTOLATINA</option>
                            <option value="6">LNG</option>
                            <option value="7">CBA</option>
                            <option value="8">MSAM</option>
                            <option value="9">BLPARTS</option>
                            <option value="100">OUTROS</option>
                        </Select>
                    </FormControl>
                    
                    <FormControl as="fieldset" borderWidth="1px" p={3} borderRadius="md" borderColor="orange.200" bg="orange.50">
                        <FormLabel as="legend" fontWeight="bold" fontSize="sm" color="orange.600" px={1}>Estratégia de Precificação</FormLabel>
                        <RadioGroup value={formData.tipo_precificacao} onChange={(val) => setFormData({...formData, tipo_precificacao: val})}>
                            <Stack>
                                <Radio value="automatica" colorScheme="orange">
                                    <Text fontSize="sm"><b>Automática:</b> Valor informado é o novo <b>Custo de Compra</b>.</Text>
                                </Radio>
                                <Radio value="manual" colorScheme="orange">
                                    <Text fontSize="sm"><b>Direta:</b> Valor informado é o novo <b>Preço de Venda</b>.</Text>
                                </Radio>
                            </Stack>
                        </RadioGroup>
                    </FormControl>

                    <Flex gap={4} w="100%" direction={{ base: "column", md: "row" }}>
                        <FormControl isRequired>
                            <FormLabel>Início</FormLabel>
                            <Input type="date" value={formData.data_inicio} onChange={(e) => setFormData({...formData, data_inicio: e.target.value})} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Fim</FormLabel>
                            <Input type="date" value={formData.data_fim} onChange={(e) => setFormData({...formData, data_fim: e.target.value})} />
                        </FormControl>
                    </Flex>
                </VStack>
            </Box>

            <Box flex="2" bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="sm">
                <Tabs variant="enclosed" colorScheme="orange" isFitted>
                    <TabList mb={4} flexDirection={{ base: "column", md: "row" }}>
                        <Tab fontWeight="bold" mb={{ base: 2, md: 0 }}>Inserção Manual</Tab>
                        <Tab fontWeight="bold">Importar Arquivo</Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel px={0}>
                            <Box mb={4} position="relative" mt={2}>
                                <FormLabel>Buscar Produto</FormLabel>
                                <InputGroup>
                                    <Input placeholder="Nome, código global ou SKU..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); if(selectedProduct) setSelectedProduct(null); }} bg="gray.50" />
                                    <InputRightElement>{isSearching ? <Spinner size="sm" /> : <SearchIcon color="gray.400"/>}</InputRightElement>
                                </InputGroup>

                                {(isSearching || (filteredProducts.length > 0 && !selectedProduct)) && (
                                    <Box position="absolute" zIndex="999" bg="white" border="1px solid #e2e8f0" w="100%" maxHeight="400px" overflowY="auto" borderRadius="md" shadow="xl" mt={1}>
                                            {isSearching ? (
                                                <VStack p={4} spacing={4} align="stretch">
                                                    {[1,2].map(i => <Skeleton key={i} height="50px" borderRadius="md"/>)}
                                                </VStack>
                                            ) : (
                                                filteredProducts.map(prod => (
                                                    <Flex key={prod.produto_id} p={3} borderBottom="1px solid #f0f0f0" _hover={{ bg: "orange.50", cursor: "pointer" }} onClick={() => handleSelectProduct(prod)} align="start" gap={4}>
                                                        <Box boxSize="60px" flexShrink={0} borderRadius="md" overflow="hidden" border="1px solid #eee">
                                                            <ProdutoImagem produto={prod} width="100%" height="100%" />
                                                        </Box>
                                                        <Box flex={1}>
                                                            <Text fontWeight="bold" fontSize="sm" mb={1} color="gray.700">{prod.nome}</Text>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={1} fontSize="xs" color="gray.500">
                                                                <GridItem><b>SKU:</b> {prod.produto_id}</GridItem>
                                                                <GridItem><b>Global:</b> {prod.codigo_global}</GridItem>
                                                                <GridItem><b>Forn:</b> {prod.codigo_fornecedor || "-"}</GridItem>
                                                                <GridItem><b>Fab:</b> {prod.fabricante_nome}</GridItem>
                                                            </Grid>
                                                            <Flex mt={2} gap={2} fontSize="xs" wrap="wrap">
                                                                <Badge colorScheme="blue">Venda: R$ {prod.valor_venda}</Badge>
                                                                <Badge colorScheme="purple">Compra: R$ {prod.valor_compra}</Badge>
                                                            </Flex>
                                                        </Box>
                                                    </Flex>
                                                ))
                                            )}
                                    </Box>
                                )}
                            </Box>

                            <Alert status="info" mb={4} fontSize="sm" borderRadius="md">
                                <AlertIcon />
                                {formData.tipo_precificacao === 'automatica' ? "Automático: O valor inserido é o novo Custo." : "Direto: O valor inserido é o Preço Final de Venda."}
                            </Alert>

                            <Flex gap={3} align="flex-end" mb={6} bg="gray.50" p={4} borderRadius="md" direction={{ base: "column", sm: "row" }}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Desconto (%)</FormLabel>
                                    <NumberInput min={0} max={100} value={descontoInput} onChange={(val) => { setDescontoInput(val); setPrecoFinalInput(""); }}>
                                        <NumberInputField bg="white" />
                                    </NumberInput>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Valor (R$)</FormLabel>
                                    <NumberInput min={0} value={precoFinalInput} onChange={(val) => { setPrecoFinalInput(val); setDescontoInput(""); }}>
                                        <NumberInputField bg="white" />
                                    </NumberInput>
                                </FormControl>
                                <Button leftIcon={<CheckIcon />} colorScheme="blue" onClick={handleAddManualItem} isDisabled={!selectedProduct} flexShrink={0} minW="140px">
                                    Adicionar
                                </Button>
                            </Flex>

                            <Divider my={4} />
                            
                            <Box overflowX="auto">
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Produto</Th>
                                            <Th>SKU</Th>
                                            <Th>Cód. Forn.</Th>
                                            <Th isNumeric>Original</Th>
                                            <Th isNumeric>Desc.</Th>
                                            <Th isNumeric>Final</Th>
                                            <Th width="50px"></Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {manualItems.map((item, idx) => (
                                            <Tr key={idx}>
                                                <Td>
                                                    <Flex align="center" gap={2}>
                                                        <Box boxSize="40px" minW="40px" borderRadius="md" overflow="hidden" border="1px solid #eee">
                                                            <ProdutoImagem produto={item} width="100%" height="100%" />
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize="xs" fontWeight="bold" noOfLines={2}>{item.nome}</Text>
                                                            <Text fontSize="2xs" color="gray.500">{item.codigo_global}</Text>
                                                        </Box>
                                                    </Flex>
                                                </Td>
                                                <Td>{item.produto_id}</Td>
                                                <Td>{item.codigo_fornecedor || "-"}</Td>
                                                <Td isNumeric>R$ {item.preco_original}</Td>
                                                <Td isNumeric color="blue.500">{item.desconto_percentual}%</Td>
                                                <Td isNumeric fontWeight="bold">R$ {item.preco_final}</Td>
                                                <Td><IconButton icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleRemoveItem(idx)} /></Td>
                                            </Tr>
                                        ))}
                                        {manualItems.length === 0 && <Tr><Td colSpan={7} textAlign="center" py={4} color="gray.500">Nenhum item adicionado.</Td></Tr>}
                                    </Tbody>
                                </Table>
                            </Box>
                        </TabPanel>

                        <TabPanel>
                            <VStack spacing={6} align="start" p={4} border="1px dashed" borderColor="gray.300" borderRadius="md">
                                <Text fontWeight="bold">Importação via Planilha</Text>
                                <Alert status="warning" fontSize="sm" borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        A coluna <b>VALOR</b> será interpretada como 
                                        <b> {formData.tipo_precificacao === 'automatica' ? 'NOVO CUSTO DE COMPRA' : 'NOVO PREÇO DE VENDA'}</b>.
                                    </Box>
                                </Alert>
                                <Button leftIcon={<DownloadIcon />} variant="outline" size="sm" onClick={handleDownloadModel}>Baixar Modelo.csv</Button>
                                
                                <Box 
                                    w="100%" 
                                    h="120px" 
                                    border="2px dashed" 
                                    borderColor={isDragOver ? "orange.400" : "gray.300"} 
                                    borderRadius="md" 
                                    bg={isDragOver ? "orange.50" : "gray.50"}
                                    display="flex" 
                                    flexDirection="column" 
                                    alignItems="center" 
                                    justifyContent="center"
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    cursor="pointer"
                                    onClick={() => fileInputRef.current.click()}
                                    transition="all 0.2s"
                                >
                                    <AttachmentIcon boxSize={6} color="gray.400" mb={2} />
                                    <Text fontSize="sm" color="gray.600" fontWeight="bold">
                                        {arquivo ? arquivo.name : "Clique ou arraste o arquivo aqui"}
                                    </Text>
                                    <Input type="file" accept=".csv,.txt" display="none" ref={fileInputRef} onChange={(e) => setArquivo(e.target.files[0])} />
                                </Box>
                            </VStack>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </Flex>

        <Flex mt={8} justify="flex-end" gap={4} borderTop="1px solid #e2e8f0" pt={6}>
            <Button size="lg" variant="ghost" onClick={() => navigate("/promocoes")}>Cancelar</Button>
            {loading ? <Preloader /> : (
                <Button size="lg" colorScheme="green" onClick={handleSubmit}>Salvar Lista</Button>
            )}
        </Flex>
      </Container>
    </Box>
  );
};

export default NovaListaPromocao;